import { JceHomepageGeographyTier, useGetAllGeographiesQuery } from 'generated/graphql';
import { TIER_LABELS, TIER_ORDER } from 'components/jceGeographyDetail/tierDefinitions';
import { TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { TextField } from 'ui';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-v7';
import Button from 'ui/Button';
import ClowncarBodyV2 from 'components/ClowncarBodyV2';
import ClowncarHeaderV2 from 'components/ClowncarHeaderV2';
import PriorityEditToggle from 'components/jceGeographyDetail/PriorityEditToggle';
import React from 'react';

export const ALL_GEOGRAPHIES = gql`
  query GetAllGeographies {
    allGeographies {
      id
      name
      urlSlug
      resyBlogKey
      tier
      priorityWithinTier
    }
  }
`;

type Geography = NonNullable<
  ReturnType<typeof useGetAllGeographiesQuery>['data']
>['allGeographies'][number];

function buildPendingUpdates(
  geographies: Geography[],
  draftPriorities: Record<number, number | ''>
) {
  return geographies
    .filter((geo) => Number(draftPriorities[geo.id] ?? '0') !== (geo.priorityWithinTier ?? 0))
    .map((geo) => ({
      id: geo.id,
      name: geo.name ?? '',
      urlSlug: geo.urlSlug ?? '',
      resyBlogKey: geo.resyBlogKey ?? '',
      tier: geo.tier,
      // For pendingUpdates, we do collapse empty string to 0; this is just a data
      // representation, not used by UI.
      priorityWithinTier: draftPriorities[geo.id] || 0,
    }));
}

const COLUMN_COUNT = 4;

const JceHomepageGeographyListRoute: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, refetch } = useGetAllGeographiesQuery();

  const [isUnlocked, setIsUnlocked] = React.useState(false);
  const [draftPriorities, setDraftPriorities] = React.useState<Record<number, number | ''>>({});

  const handleCreate = () => {
    navigate('/jce-homepage-geography/create');
  };

  const handleToggle = (unlocked: boolean) => {
    if (unlocked) {
      const initial: Record<number, number | ''> = {};
      for (const geo of data?.allGeographies ?? []) {
        initial[geo.id] = geo.priorityWithinTier ?? 0;
      }
      setDraftPriorities(initial);
    }
    setIsUnlocked(unlocked);
  };

  const pendingUpdates = React.useMemo(
    () => buildPendingUpdates(data?.allGeographies ?? [], draftPriorities),
    [data, draftPriorities]
  );

  const geographiesByTier = React.useMemo(() => {
    const result: Record<JceHomepageGeographyTier, NonNullable<typeof data>['allGeographies']> = {
      TIER_ONE: [],
      TIER_TWO: [],
      TIER_WINE_REGION: [],
      TIER_THREE: [],
      TIER_UNSPECIFIED: [],
    };
    for (const geo of data?.allGeographies ?? []) {
      result[geo.tier ?? 'TIER_UNSPECIFIED'].push(geo);
    }
    return result;
  }, [data]);

  return (
    <div className="Content">
      <ClowncarHeaderV2
        heading="JCE Homepage Geographies"
        actions={
          <Button color="primary" onClick={handleCreate} variant="contained">
            New
          </Button>
        }
      />
      <ClowncarBodyV2 isLoading={loading}>
        <TableContainer>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>URL Slug</TableCell>
              <TableCell>Resy Blog Key</TableCell>
              <TableCell>Priority Within Tier</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...TIER_ORDER, 'TIER_UNSPECIFIED' as const].map((tier) => {
              const geos = geographiesByTier[tier];
              if (geos.length === 0) {
                return null;
              }
              return (
                <React.Fragment key={tier}>
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT + 1}
                      sx={{ fontWeight: 'bold', backgroundColor: 'action.hover', py: 0.75 }}
                    >
                      {TIER_LABELS[tier]}
                    </TableCell>
                  </TableRow>
                  {geos.map((geo) => (
                    <TableRow
                      key={geo.id}
                      hover={!isUnlocked}
                      style={{ cursor: isUnlocked ? 'default' : 'pointer' }}
                      onClick={
                        isUnlocked ? undefined : () => navigate(`/jce-homepage-geography/${geo.id}`)
                      }
                    >
                      <TableCell>{geo.id}</TableCell>
                      <TableCell>{geo.name}</TableCell>
                      <TableCell>{geo.urlSlug}</TableCell>
                      <TableCell>{geo.resyBlogKey}</TableCell>
                      <TableCell>
                        {isUnlocked ? (
                          <TextField
                            size="small"
                            type="number"
                            value={draftPriorities[geo.id] ?? 0}
                            onChange={(e) =>
                              setDraftPriorities((prev) => ({
                                ...prev,
                                [geo.id]: e.target.value === '' ? '' : Number(e.target.value),
                              }))
                            }
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: 80 }}
                            inputProps={{ style: { padding: '0 8px', maxHeight: 20 } }}
                          />
                        ) : (
                          geo.priorityWithinTier
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </TableContainer>
        <PriorityEditToggle
          isUnlocked={isUnlocked}
          onUnlockedChanged={handleToggle}
          pendingUpdates={pendingUpdates}
          onSaveComplete={refetch}
        />
      </ClowncarBodyV2>
    </div>
  );
};

export default JceHomepageGeographyListRoute;
